import {
  Box,
  Button,
  Stack,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  Edit,
  SimpleForm,
  Labeled,
  useEditContext,
  useUpdate,
  useNotify,
  ReferenceManyField,
  useRecordContext,
  Toolbar,
  SaveButton,
  usePermissions,
  useGetIdentity,
} from 'react-admin';
import { rolePermission } from '../../utils';
import { EventFields } from './components/EventFields';
import { ReportsDataGrid } from '../ReportsList';

export const EventEdit = () => {
  return (
    <Edit title={<Title />}>
      <SimpleForm
        toolbar={
          <Toolbar>
            <SaveButton />
          </Toolbar>
        }
      >
        <EventInfo />
        <Typography marginTop={4} variant="h5">
          Reports:
        </Typography>
        <ReferenceManyField reference="/reports" target="id">
          <ReportsDataGrid />
        </ReferenceManyField>
      </SimpleForm>
    </Edit>
  );
};

const Title = () => {
  const record = useRecordContext();
  return <span>Event {record ? `"${record.name}"` : ''}</span>;
};

const EventInfo = () => {
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();

  const { record, isLoading, refetch } = useEditContext();
  const [update] = useUpdate();
  const [moderatorComment, setModeratorComment] = useState<string>('');
  const notify = useNotify();

  if (isLoading) return null;

  const handleActionClick = async (method: 'approve' | 'reject' | 'refine') => {
    await update(
      '/events',
      {
        id: record.id,
        data: {
          method,
          moderatorComment,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          refetch?.();
          notify('Successfully updated the event', { type: 'success' });
          setModeratorComment('');
        },
        onError: () => {
          notify(`Error happened during the record update`, { type: 'error' });
        },
      },
    );
  };

  const isEditPermitted = rolePermission.eventsEditDelete(permissions);
  const isEventCreatedByCurrentUser = record?.userCreated?.id === identity?.id;

  return (
    <>
      {isEditPermitted || isEventCreatedByCurrentUser ? (
        <EventFields viewType="edit" />
      ) : null}
      <Box mb={2}>
        <Stack gap={1}>
          <Labeled label="Status">
            <Typography variant="body1">{record.status}</Typography>
          </Labeled>

          <Labeled label="Reported">
            <Typography variant="body1">
              {record.isReported ? 'yes' : 'no'}
            </Typography>
          </Labeled>

          {record.reports?.[0] && (
            <>
              <Labeled label="Reason">
                <Typography variant="body1">
                  {record.reports[0].description}
                </Typography>
              </Labeled>
              <Labeled label="Responsible">
                <Typography variant="body1">
                  {record.reports[0].userCreated.nickname}
                </Typography>
              </Labeled>
            </>
          )}
        </Stack>

        <Stack mt={2} gap={1} width="400px">
          <Labeled label="Moderator comment">
            <TextareaAutosize
              minRows={2}
              value={moderatorComment}
              onChange={(event) => setModeratorComment(event.target.value)}
            ></TextareaAutosize>
          </Labeled>

          <Stack spacing={1} direction="row">
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleActionClick('approve')}
              color="success"
              disabled={record.status === 'Approved'}
            >
              Approve
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleActionClick('refine')}
              color="warning"
              disabled={!moderatorComment}
            >
              In review
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleActionClick('reject')}
              color="error"
              disabled={!moderatorComment || record.status === 'Rejected'}
            >
              Reject
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};
